import React, { useMemo } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/core/database/supabase';
import { useAuth } from '@/core/auth/AuthContext';
import { enforceSettingsPermission } from '@/modules/settings/permissions/enforceSettingsPermission';
import { useSystemSettings } from './hooks/useSystemSettings';
import { SettingsForm, SettingsInfoSidebar, type SettingsFormState } from './components';

export function GeneralSettingsPage() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const { form, activities, version, isLoading, canSave, updateForm, setVersion } = useSystemSettings(companyId, profile?.id);
  const [isSaving, setIsSaving] = React.useState(false);

  const saveSettings = async () => {
    if (!canSave || isSaving) return;
    setIsSaving(true);

    try {
      await enforceSettingsPermission('settings.system.update', { companyId });
      const { error: companyError } = await supabase
        .from('companies')
        .update({
          name: form.systemName,
          description: form.systemDescription,
          logo_path: form.logoPath || null,
          settings: {
            timezone: form.timezone,
            language: form.language,
            date_format: form.dateFormat,
            default_work_status: form.defaultStatus,
            upcoming_days: form.upcomingDays,
            overdue_reminder: form.overdueReminder,
            reminder_time: form.reminderTime,
            allow_duplicate_assignments: form.allowDuplicateAssignments,
            auto_status_update: form.autoStatusUpdate,
            max_file_size: form.maxFileSize,
            allowed_file_types: form.allowedFileTypes,
            storage_used_gb: form.storageUsedGb,
            storage_limit_gb: form.storageLimitGb,
            show_quick_tips: form.showQuickTips,
            dark_mode: form.darkMode,
            collapse_sidebar: form.collapseSidebar,
          },
          updated_at: new Date().toISOString(),
        })
        .eq('id', companyId);

      if (companyError) throw companyError;

      const { error: auditError } = await supabase.from('audit_logs').insert({
        company_id: companyId,
        actor_user_id: profile?.id || null,
        action_code: 'UPDATE',
        module_code: 'settings',
        entity_type: 'company_settings',
        entity_id: companyId,
        summary: 'đã cập nhật thông tin hệ thống',
        after_data: form,
      });
      if (auditError) console.warn('[GeneralSettingsPage] audit log fallback:', auditError);

      // Dispatch event to sync sidebar logo and name globally
      window.dispatchEvent(new CustomEvent('company_settings_updated', {
        detail: { name: form.systemName, logo_path: form.logoPath }
      }));

      setVersion((current) => ({ ...current, lastUpdate: new Date().toISOString() }));
      toast.success('Đã lưu thay đổi');
    } catch (error) {
      console.error('[GeneralSettingsPage] save error:', error);
      toast.error('Không thể lưu thay đổi');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    saveSettings();
  };

  if (isLoading) {
    return (
      <div className="grid gap-4 lg:grid-cols-[1fr_280px]">
        <div className="h-96 animate-pulse rounded-md bg-white p-3 shadow-sm" />
        <div className="h-80 animate-pulse rounded-md bg-white p-3 shadow-sm" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow-sm border border-slate-200">
        <SettingsForm 
          form={form} 
          onChange={updateForm} 
          onSubmit={handleSubmit} 
          onSave={saveSettings} 
          isSaving={isSaving} 
        />
      </div>
      <SettingsInfoSidebar version={version} activities={activities} />
    </div>
  );
}
